import React, { useState } from 'react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Save, X } from 'lucide-react';

interface MasterDataFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: Record<string, string>) => void;
  title: string;
  fields: { key: string; label: string; type?: 'text' | 'number' | 'select'; options?: { value: string; label: string }[]; required?: boolean }[];
  initialData?: Record<string, string>;
  isLoading?: boolean;
}

export const MasterDataForm: React.FC<MasterDataFormProps> = ({
  isOpen, onClose, onSave, title, fields, initialData, isLoading,
}) => {
  const [formData, setFormData] = useState<Record<string, string>>(initialData || {});

  const handleChange = (key: string, value: string) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  };

  const handleSubmit = () => {
    onSave(formData);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title}>
      <div className="space-y-4">
        {fields.map(field => (
          <div key={field.key}>
            <label className="block text-xs font-medium text-slate-400 mb-1">
              {field.label} {field.required && <span className="text-rose-400">*</span>}
            </label>
            {field.type === 'select' && field.options ? (
              <select
                value={formData[field.key] || ''}
                onChange={e => handleChange(field.key, e.target.value)}
                className="w-full bg-slate-800 border border-slate-700 rounded-lg p-2.5 text-xs text-slate-100 focus:outline-none focus:border-sky-500"
              >
                <option value="">Select {field.label}</option>
                {field.options.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            ) : (
              <Input
                value={formData[field.key] || ''}
                onChange={e => handleChange(field.key, e.target.value)}
                placeholder={`Enter ${field.label.toLowerCase()}`}
                type={field.type === 'number' ? 'number' : 'text'}
              />
            )}
          </div>
        ))}
      </div>
      <div className="flex justify-end gap-2 mt-6 pt-4 border-t border-slate-800">
        <Button variant="ghost" onClick={onClose} icon={<X className="w-4 h-4" />}>Cancel</Button>
        <Button variant="primary" onClick={handleSubmit} isLoading={isLoading} icon={<Save className="w-4 h-4" />}>Save</Button>
      </div>
    </Modal>
  );
};
